import { v4 as uuid } from 'uuid';
import type { GetAudienceQuery } from '../../../../graphql/query/audiences/GetAudience.generated';
import { mapAudienceShareToForm } from '../../utils/mapAudienceShareToForm';
import type { ExportTypeValue } from '../constants';
import { MatchingOperator } from '../constants';
import type { AudienceForm, FormSegment, Group, GroupLike, LookalikeFormSegment, SegmentGroupItem } from '../types';
import { mapExportTargetsToRecord } from './mapExportTargetsToRecord';

type AudienceSegmentWithMetadata = NonNullable<
  NonNullable<GetAudienceQuery['audience_by_pk']>['audience_segments'][number]['segment']
>;

const enrichSegmentGroup = <T extends FormSegment | LookalikeFormSegment>(
  segmentGroup: SegmentGroupItem<T> | undefined,
  segmentsById: Map<string, AudienceSegmentWithMetadata>
): SegmentGroupItem<T> | undefined => {
  if (!segmentGroup?.segments?.length) {
    return segmentGroup;
  }

  return {
    ...segmentGroup,
    segments: segmentGroup.segments.map(segment => ({
      ...segment,
      ...segmentsById.get(segment.id),
    })),
  };
};

const enrichGroup = (group: GroupLike, segmentsById: Map<string, AudienceSegmentWithMetadata>): GroupLike => {
  if ('groups' in group) {
    return {
      ...group,
      uuid: group.uuid ?? uuid(),
      groups: group.groups.map(nestedGroup => enrichGroup(nestedGroup, segmentsById)),
    };
  }

  const resolvedGroup: Group = {
    ...group,
    uuid: group.uuid ?? uuid(),
    direct: enrichSegmentGroup(group.direct, segmentsById),
    predefined: enrichSegmentGroup(group.predefined, segmentsById),
    location: enrichSegmentGroup(group.location, segmentsById),
    demographic_age: enrichSegmentGroup(group.demographic_age, segmentsById),
    demographic_gender: enrichSegmentGroup(group.demographic_gender, segmentsById),
    lookalike: enrichSegmentGroup(group.lookalike, segmentsById),
  };

  return resolvedGroup;
};

const DEFAULT_NORMALIZED_DEFINITION: AudienceForm['normalized_definition'] = {
  includes: {
    operator: MatchingOperator.UNION,
    groups: [],
  },
  excludes: {
    operator: MatchingOperator.UNION,
    groups: [],
  },
};

const enrichNormalizedDefinition = (
  normalizedDefinition: AudienceForm['normalized_definition'] | null | undefined,
  audienceSegments: NonNullable<GetAudienceQuery['audience_by_pk']>['audience_segments']
): AudienceForm['normalized_definition'] => {
  if (!normalizedDefinition) {
    return DEFAULT_NORMALIZED_DEFINITION;
  }

  const segmentsById = new Map(
    audienceSegments
      .map(({ segment }) => segment)
      .filter((segment): segment is AudienceSegmentWithMetadata => Boolean(segment?.id))
      .map(segment => [segment.id, segment])
  );

  return {
    includes: {
      ...normalizedDefinition.includes,
      groups: normalizedDefinition.includes.groups.map(group => enrichGroup(group, segmentsById)),
    },
    excludes: {
      ...normalizedDefinition.excludes,
      groups: normalizedDefinition.excludes.groups.map(group => enrichGroup(group, segmentsById)),
    },
  };
};

export const getInitialEditValues = (audience: NonNullable<GetAudienceQuery['audience_by_pk']>): AudienceForm => {
  const shares = mapAudienceShareToForm(audience.id, audience.audience_share);
  const normalizedDefinition = enrichNormalizedDefinition(
    audience.normalized_definition,
    audience.audience_segments ?? []
  );

  return {
    name: audience.name,
    description: audience.description ?? '',
    account_id: audience.account_id,
    export_type: audience.export_type as ExportTypeValue,
    export_targets: mapExportTargetsToRecord(audience.export_targets),
    direct_audience_provider: audience.direct_audience_provider,
    normalized_definition: normalizedDefinition,
    audience_share: shares,
  };
};
